// Reading an amount of money out of another platform's export, as text, straight into integer minor units
// (ADR-0048; spec/06 §1.1). Every platform that documents its money holds it as a float in major units — ggLeap and
// Gizmo a `double`, iCafeCloud a decimal string such as "138.00" — so an importer that went through a JavaScript
// number could turn ₹1,234.50 into 123449 paise. This never does: the digits are read as digits.
//
// What it accepts, for a currency with two minor digits (INR):
//
//   "1234.50"  "1,234.50"  "1,50,000.50" (Indian grouping)  "₹ 1,234.5"  "Rs. 1,234"  "INR 1234"  "1234 INR"
//   "-120"  "−120" (a Unicode minus)  "(120)" (an accountant's negative)  "  42  "
//
// What it refuses, each with a reason the preview shows the row with:
//
//   "150.505"      more decimals than the currency has, unless they are zeros ("150.500" is ₹150.50 exactly):
//                  rounding a balance away is a decision, never a parser's accident
//   "1,2,3"        digit grouping that is neither Western (1,234,567) nor Indian (12,34,567)
//   "$12", "12 USD", "€5"   another currency's symbol or code
//   "1e3", "0x10", "12abc", "1.2.3", ""   anything that is not plainly an amount
//
// An empty cell is `{ kind: 'empty' }`, not zero: "no value" and "zero" are different facts, and the template decides
// what an empty cell means.
//
// `unit: 'minor'` reads a column already in minor units (paise): whole numbers only.
/** The currency symbols and codes read as the organisation's own currency, by ISO 4217 code. */
const CURRENCY_MARKS = {
    INR: ['₹', 'rs.', 'rs', 'inr'],
};
/** Marks of other currencies: an amount carrying one is refused rather than silently read as rupees. */
const FOREIGN_MARKS = ['$', '€', '£', '¥', 'usd', 'eur', 'gbp', 'aed', 'sgd', 'aud', 'cad', 'jpy', 'cny'];
/** The API's cap on any single amount: 10^13 minor units (spec/06 §1.1). */
export const MAX_IMPORTED_AMOUNT_MINOR = 10_000_000_000_000;
const WESTERN_GROUPING = /^\d{1,3}(,\d{3})+$/;
const INDIAN_GROUPING = /^\d{1,2}(,\d{2})*,\d{3}$/;
/** Read `text` as an amount of `options.currency`, in minor units, without ever passing through a float. */
export function parseAmountText(text, options) {
    if (text === null || text === undefined)
        return { kind: 'empty' };
    let rest = text.replace(/[\u00a0\u2007\u202f]/g, ' ').trim();
    if (rest === '')
        return { kind: 'empty' };
    const minorDigits = options.minorDigits ?? 2;
    const lower = rest.toLowerCase();
    for (const mark of FOREIGN_MARKS) {
        if (lower.includes(mark))
            return { kind: 'error', reason: 'other-currency' };
    }
    // A negative: a leading or trailing minus (ASCII or Unicode), or the whole amount in brackets.
    let negative = false;
    if (rest.startsWith('(') && rest.endsWith(')')) {
        negative = true;
        rest = rest.slice(1, -1).trim();
    }
    rest = rest.replace(/[−‒–]/g, '-');
    if (rest.startsWith('-')) {
        if (negative)
            return { kind: 'error', reason: 'not-a-number' };
        negative = true;
        rest = rest.slice(1).trim();
    }
    // The organisation's own currency mark, before or after the digits, once.
    const marks = [...(CURRENCY_MARKS[options.currency.toUpperCase()] ?? []), options.currency.toLowerCase()];
    for (const mark of marks.sort((a, b) => b.length - a.length)) {
        const lowered = rest.toLowerCase();
        if (lowered.startsWith(mark)) {
            rest = rest.slice(mark.length).trim();
            break;
        }
        if (lowered.endsWith(mark)) {
            rest = rest.slice(0, rest.length - mark.length).trim();
            break;
        }
    }
    if (rest.startsWith('-')) {
        if (negative)
            return { kind: 'error', reason: 'not-a-number' };
        negative = true;
        rest = rest.slice(1).trim();
    }
    if (rest.endsWith('-')) {
        if (negative)
            return { kind: 'error', reason: 'not-a-number' };
        negative = true;
        rest = rest.slice(0, -1).trim();
    }
    if (rest === '')
        return { kind: 'error', reason: 'not-a-number' };
    const parts = rest.split('.');
    if (parts.length > 2)
        return { kind: 'error', reason: 'not-a-number' };
    const whole = parts[0] ?? '';
    const fraction = parts[1];
    if (fraction !== undefined && !/^\d+$/.test(fraction))
        return { kind: 'error', reason: 'not-a-number' };
    if (whole === '' && fraction === undefined)
        return { kind: 'error', reason: 'not-a-number' };
    let digits;
    if (whole === '') {
        digits = '0';
    }
    else if (/^\d+$/.test(whole)) {
        digits = whole;
    }
    else if (/^[\d,]+$/.test(whole)) {
        if (!WESTERN_GROUPING.test(whole) && !INDIAN_GROUPING.test(whole))
            return { kind: 'error', reason: 'bad-grouping' };
        digits = whole.replace(/,/g, '');
    }
    else {
        return { kind: 'error', reason: 'not-a-number' };
    }
    if (options.unit === 'minor') {
        // Already paise: a fraction is only allowed if it is all zeros ("1500.00" paise is 1500 paise).
        if (fraction !== undefined && !/^0+$/.test(fraction))
            return { kind: 'error', reason: 'too-precise' };
        return finish(BigInt(digits), negative);
    }
    const fractionDigits = fraction ?? '';
    const significant = fractionDigits.replace(/0+$/, '');
    if (significant.length > minorDigits)
        return { kind: 'error', reason: 'too-precise' };
    const padded = (fractionDigits.slice(0, minorDigits) + '0'.repeat(minorDigits)).slice(0, minorDigits);
    const scale = 10n ** BigInt(minorDigits);
    return finish(BigInt(digits) * scale + (minorDigits === 0 ? 0n : BigInt(padded)), negative);
}
function finish(magnitude, negative) {
    if (magnitude > BigInt(MAX_IMPORTED_AMOUNT_MINOR))
        return { kind: 'error', reason: 'too-large' };
    const value = Number(magnitude);
    return { kind: 'ok', minor: negative && value !== 0 ? -value : value };
}
//# sourceMappingURL=amount.js.map